import { useCallback, useEffect, useState, useRef } from "react";
import axios from "axios";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { Play, Square, Plus, Trash2, X } from "lucide-react";

function fmtHM(mins) {
  const h = Math.floor(mins / 60), m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function Time() {
  const [entries, setEntries] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // Stopwatch
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0); // seconds
  const [timerJob, setTimerJob] = useState("");
  const [timerNote, setTimerNote] = useState("");
  const timerRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const [t, j] = await Promise.all([
      axios.get(`${API_BASE}/time-entries`),
      axios.get(`${API_BASE}/jobs`),
    ]);
    setEntries(t.data); setJobs(j.data); setLoading(false);
    if (!timerJob && j.data[0]) setTimerJob(j.data[0].job_id);
  }, [timerJob]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (running) {
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current); timerRef.current = null;
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [running]);

  const totalMins = entries.reduce((a, b) => a + b.minutes, 0);
  const billable = entries.filter((e) => e.billable).reduce((a, b) => a + b.minutes, 0);

  const startTimer = () => { setElapsed(0); setRunning(true); };
  const stopTimer = async () => {
    setRunning(false);
    const mins = Math.max(1, Math.round(elapsed / 60));
    if (!timerJob) return;
    try {
      await axios.post(`${API_BASE}/time-entries`, {
        job_id: timerJob, minutes: mins, billable: true,
        note: timerNote || "Tracked via timer",
      });
      setElapsed(0); setTimerNote(""); reload();
    } catch (err) { console.error(formatApiError(err)); }
  };

  const onDelete = async (id) => {
    await axios.delete(`${API_BASE}/time-entries/${id}`);
    reload();
  };

  const jobTitle = (id) => jobs.find((j) => j.job_id === id)?.title || "—";

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="time-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline mb-2">Time tracking</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>Your time</h1>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-secondary" data-testid="time-entry-create-btn">
          <Plus size={14} /> Log manually
        </button>
      </div>

      {/* Stopwatch */}
      <div className="card p-6 mb-8" data-testid="timer">
        <div className="overline mb-4">Stopwatch</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-4 items-end">
          <div>
            <label className="overline block mb-1.5">Job</label>
            <select value={timerJob} onChange={(e) => setTimerJob(e.target.value)} data-testid="timer-job"
              disabled={running}
              className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]">
              {jobs.map((j) => <option key={j.job_id} value={j.job_id}>{j.title}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-1.5">Note</label>
            <input value={timerNote} onChange={(e) => setTimerNote(e.target.value)} data-testid="timer-note"
              placeholder="What are you working on?"
              className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
          </div>
          <div className="flex items-end gap-3">
            <div className="font-heading text-3xl tabular px-3" style={{ fontWeight: 500, color: running ? "var(--accent)" : "var(--ink)" }}
                 data-testid="timer-display">
              {String(Math.floor(elapsed / 3600)).padStart(2, "0")}:
              {String(Math.floor((elapsed % 3600) / 60)).padStart(2, "0")}:
              {String(elapsed % 60).padStart(2, "0")}
            </div>
            {!running ? (
              <button onClick={startTimer} disabled={!timerJob} className="btn-primary" data-testid="timer-start">
                <Play size={14} /> Start
              </button>
            ) : (
              <button onClick={stopTimer} className="btn-secondary" data-testid="timer-stop">
                <Square size={14} /> Stop & log
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)] rounded-md overflow-hidden mb-8">
        <Stat label="Total logged" value={fmtHM(totalMins)} testid="time-stat-total" />
        <Stat label="Billable" value={fmtHM(billable)} sub={`${totalMins ? Math.round((billable / totalMins) * 100) : 0}% billable`} testid="time-stat-billable" />
        <Stat label="Entries" value={entries.length} testid="time-stat-count" />
      </div>

      {loading ? (
        <div className="text-[var(--ink-3)]">Loading…</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="report-table">
            <thead>
              <tr><th>Date</th><th>Job</th><th>Duration</th><th>Note</th><th>Billable</th><th></th></tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.time_entry_id} data-testid={`time-entry-row-${e.time_entry_id}`}>
                  <td className="font-mono text-[12px]">{fmtDate(e.entry_date)}</td>
                  <td className="text-[var(--ink)] font-medium">{jobTitle(e.job_id)}</td>
                  <td className="tabular text-[var(--accent)] font-medium">{fmtHM(e.minutes)}</td>
                  <td className="text-[13px]">{e.note || "—"}</td>
                  <td>
                    <span className="badge" style={{ background: e.billable ? "var(--moss)" : "var(--ink-3)", color: "var(--paper)" }}>
                      {e.billable ? "billable" : "non"}
                    </span>
                  </td>
                  <td>
                    <button onClick={() => onDelete(e.time_entry_id)} className="text-[var(--ink-3)] hover:text-[var(--accent)]"
                            data-testid={`time-entry-delete-${e.time_entry_id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr><td colSpan="6" className="text-center text-[var(--ink-3)] py-10">No entries yet. Start the stopwatch above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <TimeModal jobs={jobs} onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); reload(); }} />
      )}
    </div>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="bg-[var(--paper)] p-6" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-heading text-3xl tabular mt-2" style={{ fontWeight: 500 }}>{value}</div>
      {sub && <div className="text-[12px] text-[var(--ink-3)] mt-1">{sub}</div>}
    </div>
  );
}

function TimeModal({ jobs, onClose, onCreated }) {
  const [form, setForm] = useState({
    job_id: jobs[0]?.job_id || "", minutes: 30, billable: true,
    note: "", entry_date: new Date().toISOString().slice(0, 10),
  });
  const [error, setError] = useState(""); const [saving, setSaving] = useState(false);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await axios.post(`${API_BASE}/time-entries`, { ...form, minutes: Number(form.minutes) });
      onCreated();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="time-modal"
         style={{ background: "rgba(26,24,20,0.45)" }}>
      <div className="bg-[var(--paper)] border border-[var(--border)] rounded-md max-w-lg w-full p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline">Log time</div>
            <h3 className="font-heading text-2xl mt-1" style={{ fontWeight: 500 }}>Add a time entry</h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="time-modal-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="time-form">
          <div>
            <label className="overline block mb-1.5">Job</label>
            <select value={form.job_id} onChange={(e) => upd("job_id")(e.target.value)} required data-testid="time-form-job"
              className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]">
              {jobs.map((j) => <option key={j.job_id} value={j.job_id}>{j.title}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1.5">Date</label>
              <input type="date" value={form.entry_date} onChange={(e) => upd("entry_date")(e.target.value)} data-testid="time-form-date"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
            </div>
            <div>
              <label className="overline block mb-1.5">Minutes</label>
              <input type="number" min="1" value={form.minutes} onChange={(e) => upd("minutes")(e.target.value)} required data-testid="time-form-minutes"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
            </div>
          </div>
          <div>
            <label className="overline block mb-1.5">Note</label>
            <input value={form.note} onChange={(e) => upd("note")(e.target.value)} data-testid="time-form-note"
              className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
          </div>
          <label className="flex items-center gap-2 text-[14px]">
            <input type="checkbox" checked={form.billable} onChange={(e) => upd("billable")(e.target.checked)} data-testid="time-form-billable" />
            Billable
          </label>
          {error && <div className="text-[13px] text-[var(--accent)]" data-testid="time-form-error">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center" data-testid="time-form-submit">
              {saving ? "Saving…" : "Log time"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
